import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { Layout, ProgressBar, TextStyle } from "@shopify/polaris";
import { useApolloClient } from "@apollo/react-hooks";
import Moment from "moment";
// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";
// import context
import { PrivateContext } from "lib/context";
// import constant
import constant from "lib/constant/constant";
// import components
import { Banner, Spinner } from "lib/components";
// import query
import { GET_PRODUCT_EXPORT_SETTING } from "app/setup/apollo/queries";
import { GET_PRODUCT_DETAILS } from "app/productOld/apollo/queries";
// import hooks
import { useBatchExport } from "lib/hooks";
// import helpers
import { baseHelper } from "lib/helpers";
import productHelper from "./helper/product";

const ExportAsCSV = () => {
  const data = useContext(PrivateContext);
  const { cms } = data;
  const client = useApolloClient();
  const hasStartedRef = useRef(false);
  const settingResponseRef = useRef(null);
  const [settingResponse, setSettingResponse] = useState(null);
  const [settingError, setSettingError] = useState(false);

  const [banner, setBanner] = useState({
    title: "",
    status: "",
    isOpen: false,
  });

  const showErrorBanner = useCallback((title) => {
    setBanner({ title, status: constant.CRITICAL, isOpen: true });
    setTimeout(() => window.close(), 2000);
  }, []);

  const fetchPage = useCallback(
    (page, filters) =>
      client
        .query({
          query: GET_PRODUCT_DETAILS,
          variables: { input: { ...filters, page } },
          fetchPolicy: "network-only",
        })
        .then((res) => {
          const response = res.data.getProductDetail;
          if (response.status !== constant.OK) {
            throw new Error(response.error || cms("section.export.message.error.noProduct"));
          }
          const { productList = [], hasMore = false, totalCount } = response.data || {};
          return { rows: productList, hasMore, totalCount };
        }),
    [client, cms]
  );

  const mapRows = useCallback(
    (rows) => productHelper.productCSVData(rows, settingResponseRef.current, true, false, false, {}, false),
    []
  );

  const getFileName = useCallback(() => {
    const date = Moment(new Date(), "DD-MM-YYYY").format("LL");
    return `${constant.CONSOLIDATED_PRODUCT} ${date}.csv`;
  }, []);

  const onError = useCallback((err) => showErrorBanner(err.message || cms("section.export.message.error.noProduct")), [
    showErrorBanner,
    cms,
  ]);

  const { status: exportStatus, fetchedCount, totalCount, progress, start } = useBatchExport({
    fetchPage,
    mapRows,
    getFileName,
    onError,
  });

  useEffect(() => {
    if (exportStatus === "empty") {
      showErrorBanner(cms("section.export.message.error.noProduct"));
    }
    if (exportStatus === "done") {
      setTimeout(() => window.close(), 500);
    }
  }, [exportStatus, showErrorBanner, cms]);

  useEffect(() => {
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;

    client
      .query({ query: GET_PRODUCT_EXPORT_SETTING, fetchPolicy: "network-only" })
      .then((res) => {
        const response = res.data.getProductExportSetting;
        if (response.status !== constant.OK) {
          setSettingError(true);
          showErrorBanner(response.error || cms("section.export.message.error.noProduct"));
          return;
        }
        settingResponseRef.current = response.data;
        setSettingResponse(response.data);

        const { endDate, startDate, status, vendor } = baseHelper.queryParamsFromLocation(data);
        const filters = {};
        if (status && status.length) {
          filters.status = status.split(",");
        }
        if (vendor && vendor.length) {
          filters.vendor = vendor.split(",");
        }
        if (startDate && endDate) {
          filters.dateRange = { start: startDate, end: endDate };
        }
        start(filters);
      })
      .catch((err) => {
        setSettingError(true);
        showErrorBanner(err.message || cms("section.export.message.error.noProduct"));
      });
  }, [data, client, cms, showErrorBanner, start]);

  return (
    <Layout.Section>
      {!settingResponse && !settingError && <Spinner isFullPage />}
      {exportStatus === "loading" && (
        <div className="fullPageProgress">
          <ProgressBar progress={progress} size="medium" />
          <br />
          <TextStyle variation="subdued">
            {totalCount ? `Exporting ${fetchedCount} of ${totalCount} records...` : "Preparing export..."}
          </TextStyle>
        </div>
      )}
      {banner.isOpen && (
        <Banner
          title={banner.title}
          status={banner.status}
          isOpen={banner.isOpen}
          onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
        />
      )}
    </Layout.Section>
  );
};
export default withFeature(withErrorBoundary(ExportAsCSV), { feature: constant.EXPORT_PRODUCT });
